import { selectFeatureFlags, selectUserContext } from 'core/userContext';
import { closeNav, openNav } from 'modules/nav/ducks/actions';
import { NavButton } from 'modules/nav/components/NavButton';
import { selectNavComponentKey } from 'modules/nav/ducks/selectors';
import { createNavbarMenus } from 'modules/nav/utils';
import { fetchNotificationCountRequest } from 'modules/notificationCenter/actions';
import { selectUnreadNotificationsCount } from 'modules/notificationCenter/selectors';
import { useQuickActionsMenuContext } from 'modules/quickActionsMenu/contexts';
import { PAGE } from 'modules/quickActionsMenu/types';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { ReactNode, SyntheticEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ActionMenu } from 'ui/actionMenu';
import { ActionMenuOption, AllowedActionMenuElements } from 'ui/actionMenu/types';
import { duration, spacing } from 'ui/theme';
import { TipPlacement, Tooltip } from 'ui/tooltip';
import { camelCase } from 'utils/stringHelpers';
import { SLIDER_MENUS } from '../../constants';
import { openChat } from '../../utils/supportChat';
import { useAuthenticateZendeskChat } from '../../hooks/useAuthenticateZendeskChat';
import { NotificationIndicator } from './NotificationIndicator';

const NavbarMenusRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

export const NAVBAR_MENUS_TEXT = {
  id: 'navbar-menus',
  iconId: 'navbar-menus-icon',
};

type Wrapper = {
  children: ReactNode;
  id: string;
  options: ActionMenuOption[];
};

const ButtonWrapper = ({ children, id, options }: Wrapper) => {
  if (options.length > 0) {
    return (
      <ActionMenu id={id} options={options}>
        {children}
      </ActionMenu>
    );
  }

  return <div>{children}</div>;
};

export const NavbarMenus = () => {
  const dispatch = useDispatch();
  const navComponentKey = useSelector(selectNavComponentKey);
  const userContext = useSelector(selectUserContext);
  const { SupportChatAgentWorkspace, SupportChatAgentWorkspaceAuth } =
    useSelector(selectFeatureFlags);
  const unreadNotifications = useSelector(selectUnreadNotificationsCount);
  const navLinks = createNavbarMenus(userContext);
  const { selectPage } = useQuickActionsMenuContext();

  const handleClick = (event: SyntheticEvent, navKey?: string) => {
    if (!navKey) return undefined;

    switch (navKey) {
      case SLIDER_MENUS.OPEN_CHAT: {
        dispatch(closeNav());
        try {
          openChat(Boolean(userContext.showLiveChat), SupportChatAgentWorkspace);
        } catch (error) {
          dispatch(addNegativeToastWithErrorId('Trouble launching chat.', 'openChat', error));
        }
        break;
      }
      case SLIDER_MENUS.OPEN_SUPPORT: {
        dispatch(closeNav());
        break;
      }
      case SLIDER_MENUS.OPEN_QUICK_ACTIONS: {
        selectPage({ page: PAGE.HOME_PAGE });
        break;
      }
      default: {
        const action = navKey === navComponentKey ? closeNav() : openNav(navKey);
        dispatch(action);
        event.stopPropagation();
      }
    }
  };

  /** Authenticate with Zendesk to supply user details, if Zendesk chat is enabled */
  useAuthenticateZendeskChat(SupportChatAgentWorkspace, SupportChatAgentWorkspaceAuth, userContext);

  useEffect(() => {
    dispatch(fetchNotificationCountRequest());
  }, [dispatch]);

  return (
    <NavbarMenusRow id={NAVBAR_MENUS_TEXT.id} data-test={NAVBAR_MENUS_TEXT.id}>
      {navLinks.map((item) => {
        const { dropdownOptions = [], Icon } = item;
        const menuItems = dropdownOptions.map(
          (option): ActionMenuOption => ({
            as: AllowedActionMenuElements.Link,
            content: option.title,
            href: option.href,
            id: camelCase(option.title),
            isExternalLink: !!option.isExternal,
          }),
        );

        return (
          <Tooltip
            delayDuration={parseInt(duration.dramatic, 10)}
            key={item.id}
            isEnabled={!!item.tooltip}
            tooltipContent={item.tooltip}
            placement={TipPlacement.Bottom}
            isUsedOnButton
          >
            <ButtonWrapper key={item.id} id={item.id} options={menuItems}>
              <NavButton
                id={item.id}
                isActive={navComponentKey === item.navKey}
                onClick={(e) => handleClick(e, item.navKey)}
              >
                <Icon
                  data-test={NAVBAR_MENUS_TEXT.iconId}
                  width={24}
                  height={24}
                  isColorInherited
                />
                <NotificationIndicator navKey={item.navKey} count={unreadNotifications} />
              </NavButton>
            </ButtonWrapper>
          </Tooltip>
        );
      })}
    </NavbarMenusRow>
  );
};
